// AdminPaymentRequests.js
import { useState, useEffect } from "react";
import axios from "axios";
import Select from "react-select";
import { Dropdown } from "react-bootstrap";
import { confirmSwal } from "../utils/confirm";
import { auth } from "../firebase";

export default function AdminPaymentRequests() {
    const [groups, setGroups] = useState([]);
    const [directory, setDirectory] = useState([]);
    const [showCreate, setShowCreate] = useState(false);
    const [newGroupData, setNewGroupData] = useState({
        group_name: "",
        description: "",
        directory_members: [],
        external_members: [],
    });
    const [externalEmail, setExternalEmail] = useState("");
    const [externalName, setExternalName] = useState("");
    const [amountInputs, setAmountInputs] = useState({});
    const [showMembersMap, setShowMembersMap] = useState({});
    const [showArchived, setShowArchived] = useState(false);
    const [sendingMap, setSendingMap] = useState({});
    const [selectedStatuses, setSelectedStatuses] = useState([]);

    useEffect(() => {
        fetchGroups();
        fetchDirectory();
    }, []);

    const fetchGroups = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/payments", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setGroups(res.data);

        const defaultShowMembers = {};
        res.data.forEach((group) => {
            const activeInvoice = group.invoices?.find((inv) => !inv.closed);
            const allPaid = activeInvoice && activeInvoice.members.every((m) => m.paid);
            defaultShowMembers[group._id] = !allPaid;
        });
        setShowMembersMap(defaultShowMembers);
    };


    const fetchDirectory = async () => {
        const token = await auth.currentUser.getIdToken();
        const res = await axios.get("/api/directory", {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        setDirectory(res.data);
    };

    // Compute unique statuses
    const statusOptions = Array.from(
        new Set(
            directory
                .map(user => user.status?.toLowerCase().trim())
                .filter(Boolean)
        )
    ).map(status => ({
        value: status,
        label: status.charAt(0).toUpperCase() + status.slice(1)
    }));

    // Whenever selectedStatuses changes, update directory_members
    useEffect(() => {
        if (selectedStatuses.length === 0) return;

        const selectedStatusValues = selectedStatuses.map(s => s.value);

        const matchingUserIds = directory
            .filter(user => selectedStatusValues.includes(user.status?.toLowerCase().trim()))
            .map(user => user._id);

        setNewGroupData(prev => ({
            ...prev,
            directory_members: Array.from(new Set([...prev.directory_members, ...matchingUserIds]))
        }));
    }, [selectedStatuses, directory]);


    const handleAddDirectoryMember = () => {
        setNewGroupData((prev) => ({
            ...prev,
            directory_members: [...prev.directory_members, ""],
        }));
    };

    const handleDirectorySelect = (index, selectedOption) => {
        const updated = [...newGroupData.directory_members];
        updated[index] = selectedOption ? selectedOption.value : "";
        setNewGroupData({ ...newGroupData, directory_members: updated });
    };

    const handleRemoveDirectoryMember = (index) => {
        const updated = [...newGroupData.directory_members];
        updated.splice(index, 1);
        setNewGroupData({ ...newGroupData, directory_members: updated });
    };

    const handleAddExternal = () => {
        if (!externalEmail) return;

        // Check validity
        const emailInput = document.createElement("input");
        emailInput.type = "email";
        emailInput.value = externalEmail;
        if (!emailInput.checkValidity()) {
            alert("Please enter a valid email address.");
            return;
        }

        const updatedExternal = [
            ...newGroupData.external_members,
            { email: externalEmail, name: externalName || "Guest" },
        ];

        setNewGroupData({ ...newGroupData, external_members: updatedExternal });
        setExternalEmail("");
        setExternalName("");
    };

    const handleCreate = async () => {
        let finalExternalMembers = [...newGroupData.external_members];
        if (externalEmail) {
            const emailInput = document.createElement("input");
            emailInput.type = "email";
            emailInput.value = externalEmail;
            if (!emailInput.checkValidity()) {
                alert("Please enter a valid email address.");
                return;
            }
            finalExternalMembers.push({
                email: externalEmail,
                name: externalName || "Guest",
            });
        }

        const payload = {
            group_name: newGroupData.group_name,
            description: newGroupData.description,
            member_ids: newGroupData.directory_members,
            external_members: finalExternalMembers,
        };

        const token = await auth.currentUser.getIdToken();
        await axios.post("/api/payments", payload, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });

        setShowCreate(false);
        setNewGroupData({
            group_name: "",
            description: "",
            directory_members: [],
            external_members: [],
        });
        setExternalEmail("");
        setExternalName("");
        fetchGroups();
    };

    const handleAmountChange = (groupId, value) => {
        setAmountInputs((prev) => ({ ...prev, [groupId]: value }));
    };

    const toggleShowMembers = (groupId) => {
        setShowMembersMap((prev) => ({ ...prev, [groupId]: !prev[groupId] }));
    };

    const handleArchive = async (id) => {
        const token = await auth.currentUser.getIdToken();
        await axios.delete(`/api/payments/${id}`, {
            headers: {
                Authorization: `Bearer ${token}`,
            },
        });
        fetchGroups();
    };

    const activeGroups = groups.filter((g) => g.active !== false);
    const archivedGroups = groups.filter((g) => g.active === false);

    const renderGroupCard = (group) => {
        const amountInput = amountInputs[group._id] || "";
        const showMembers = showMembersMap[group._id] || false;

        const activeInvoice = group.invoices?.find((inv) => !inv.closed);
        const allPaid = activeInvoice && activeInvoice.members.every((m) => m.paid);

        const handleSendInvoices = async (groupId) => {
            const cents = Math.round(parseFloat(amountInputs[groupId]) * 100);
            if (isNaN(cents) || cents <= 0) {
                alert("Please enter a valid amount");
                return;
            }

            setSendingMap((prev) => ({ ...prev, [groupId]: true }));

            try {
                const token = await auth.currentUser.getIdToken();
                await axios.post(`/api/payments/${groupId}/send_invoices`, { amount_cents: cents }, {
                    headers: {
                        Authorization: `Bearer ${token}`,
                    },
                });
                fetchGroups();
                setAmountInputs((prev) => ({ ...prev, [groupId]: "" }));
            } catch (error) {
                alert("Failed to send payment requests");
            }

            setSendingMap((prev) => ({ ...prev, [groupId]: false }));
        };


        return (
            <div key={group._id} className="card mb-3">
                <div className="card-body">
                    <div className="d-flex justify-content-between align-items-start">
                        <h5>
                            {group.group_name} <span className="text-body-secondary">({group.members.length} members)</span>
                            {group.active === false && <span className="badge bg-secondary ms-2">Archived</span>}
                        </h5>

                        <button
                            className="btn btn-outline-secondary btn-sm"
                            onClick={async () => {
                                const ok = await confirmSwal({
                                    title: `Delete group?`,
                                    text: "Once deleted it cannot be restored.",
                                    icon: "warning",
                                    confirmText: "Delete",
                                    confirmColor: "#d33",
                                });
                                if (!ok) return;
                                handleArchive(group._id);
                            }}
                            disabled={group.active === false}
                        >
                            Delete Group
                        </button>
                    </div>

                    <p>{group.description}</p>

                    {/* New separate block for status badge */}
                    <div className="mb-4">
                        {activeInvoice ? (
                            allPaid ? (
                                <>
                                    <span className="badge bg-success">
                                        All Paid (${(activeInvoice.amount_cents / 100).toFixed(2)})
                                    </span>
                                    <button
                                        className="btn btn-sm btn-custom-outline ms-2"
                                        onClick={async () => {
                                            const token = await auth.currentUser.getIdToken();
                                            await axios.post(`/api/payments/${group._id}/complete_invoice`, null, {
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                },
                                            });
                                            fetchGroups();
                                        }}
                                    >
                                        Complete
                                    </button>
                                </>
                            ) : (
                                <span className="badge bg-warning text-dark">
                                    Active Payment Request (${(activeInvoice.amount_cents / 100).toFixed(2)})
                                </span>
                            )
                        ) : (
                            <span className="badge bg-secondary">No Active Payment Requests</span>
                        )}

                    </div>

                    {!activeInvoice && (
                        <>
                            <h6>Create Payment Request</h6>
                            <div className="input-group mb-3" style={{ maxWidth: "300px" }}>
                                <input
                                    type="number"
                                    className="form-control"
                                    placeholder="Amount (USD)"
                                    value={amountInput}
                                    onChange={(e) => handleAmountChange(group._id, e.target.value)}
                                />
                                <button
                                    className="btn btn-admin"
                                    onClick={() => handleSendInvoices(group._id)}
                                    disabled={sendingMap[group._id]}
                                >
                                    {sendingMap[group._id] ? (
                                        <>
                                            <span
                                                className="spinner-border spinner-border-sm me-1"
                                                role="status"
                                                aria-hidden="true"
                                                style={{ color: "white" }}
                                            ></span>
                                            <span style={{ color: "white" }}>Sending...</span>
                                        </>
                                    ) : (
                                        "Send Requests"
                                    )}
                                </button>

                            </div>
                        </>
                    )}

                    <div className="d-flex align-items-center mb-2">
                        <h6 className="mb-0 me-2">Members</h6>
                        <button
                            className="btn btn-custom-outline btn-sm"
                            onClick={() => toggleShowMembers(group._id)}
                        >
                            {showMembers ? "Hide" : "Show"}
                        </button>
                    </div>

                    {!activeInvoice && (
                        <div className="mt-3">
                            <h6>Add Member</h6>
                            <Select
                                options={directory
                                    .filter(user => !group.members.some(m => m.email === user.email_address)) // exclude already-added
                                    .map(user => ({
                                        value: user._id,
                                        label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                    }))}
                                onChange={async (selectedOption) => {
                                    if (!selectedOption) return;

                                    try {
                                        const token = await auth.currentUser.getIdToken();
                                        await axios.post(
                                            `/api/payments/${group._id}/add_member`,
                                            { user_id: selectedOption.value },
                                            {
                                                headers: {
                                                    Authorization: `Bearer ${token}`,
                                                },
                                            }
                                        );
                                        fetchGroups();
                                    } catch (error) {
                                        alert("Failed to add member");
                                    }
                                }}
                                isClearable
                                placeholder="Select member to add..."
                                styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                            />
                        </div>
                    )}


                    {showMembers && (
                        <div>
                            <ul className="mt-2 list-group">
                                {group.members.map((m, idx) => {
                                    const invoiceMember = activeInvoice?.members.find(im => im.email === m.email);

                                    return (
                                        <li key={idx} className="list-group-item d-flex justify-content-between align-items-center">
                                            <span>
                                                {m.status && (<span className="badge bg-primary ms-0 me-2">Member: {m.status.charAt(0).toUpperCase() + m.status.slice(1)}</span>)} {m.name} ({m.email})
                                                {!m.user_id && <span className="badge bg-secondary ms-2">External</span>}
                                            </span>

                                            <div className="d-flex align-items-center">
                                                {activeInvoice && (
                                                    <span className={`badge me-2 ${invoiceMember?.paid ? "bg-success" : "bg-warning text-dark"}`}>
                                                        {invoiceMember?.paid ? "Paid" : "Unpaid"}
                                                    </span>
                                                )}

                                                <Dropdown>
                                                    <Dropdown.Toggle variant="white" size="sm" id={`dropdown-${idx}`}>
                                                        <i className="bi bi-three-dots"></i>
                                                    </Dropdown.Toggle>

                                                    <Dropdown.Menu>
                                                        {activeInvoice && !invoiceMember?.paid && (
                                                            <Dropdown.Item
                                                                onClick={async () => {
                                                                    const token = await auth.currentUser.getIdToken();
                                                                    await axios.post(
                                                                        `/api/payments/${group._id}/mark_paid`,
                                                                        { email: m.email },
                                                                        {
                                                                            headers: {
                                                                                Authorization: `Bearer ${token}`,
                                                                            },
                                                                        }
                                                                    );
                                                                    fetchGroups();
                                                                }}
                                                            >
                                                                Mark as Paid
                                                            </Dropdown.Item>
                                                        )}
                                                        {!activeInvoice && (
                                                            <Dropdown.Item
                                                                onClick={async () => {
                                                                    const token = await auth.currentUser.getIdToken();
                                                                    await axios.post(
                                                                        `/api/payments/${group._id}/remove_member`,
                                                                        { email: m.email },
                                                                        {
                                                                            headers: {
                                                                                Authorization: `Bearer ${token}`,
                                                                            },
                                                                        }
                                                                    );

                                                                    fetchGroups();
                                                                }}
                                                            >
                                                                Remove from group
                                                            </Dropdown.Item>
                                                        )}
                                                    </Dropdown.Menu>
                                                </Dropdown>
                                            </div>
                                        </li>
                                    );
                                })}
                            </ul>
                        </div>
                    )}

                </div>
            </div>
        );
    };

    return (
        <div className="container mt-4">
            <h3>Payment Groups</h3>
            <button className="btn btn-admin mb-3" onClick={() => setShowCreate(!showCreate)}>
                {showCreate ? "Cancel" : "Create New Payment Group"}
            </button>

            {showCreate && (
                <div className="card p-3 mb-4">
                    <h5>Create New Payment Group</h5>
                    <div className="mb-2">
                        <input
                            type="text"
                            className="form-control"
                            placeholder="Group Name"
                            value={newGroupData.group_name}
                            onChange={(e) => setNewGroupData({ ...newGroupData, group_name: e.target.value })}
                        />
                    </div>
                    <div className="mb-2">
                        <textarea
                            className="form-control"
                            placeholder="Description"
                            value={newGroupData.description}
                            onChange={(e) => setNewGroupData({ ...newGroupData, description: e.target.value })}
                        ></textarea>
                    </div>

                    <h6>Quick add by role:</h6>
                    <Select
                        options={statusOptions}
                        isMulti
                        value={selectedStatuses}
                        onChange={setSelectedStatuses}
                        placeholder="Select role(s)..."
                        className="mb-3"
                    />

                    <h6>Add specific directory members:</h6>
                    {newGroupData.directory_members.map((memberId, index) => (
                        <div key={index} className="d-flex mb-2 gap-2 align-items-center">
                            <div style={{ flex: "1 1 0%", minWidth: 0 }}>
                                <Select
                                    options={directory.map(user => ({
                                        value: user._id,
                                        label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                    }))}
                                    value={directory
                                        .map(user => ({
                                            value: user._id,
                                            label: `${user.surname}, ${user.first_name}${user.known_as ? ` (${user.known_as})` : ""}`
                                        }))
                                        .find(option => option.value === memberId)}
                                    onChange={(selectedOption) => handleDirectorySelect(index, selectedOption)}
                                    isClearable
                                    placeholder="Select member"
                                    styles={{ container: (base) => ({ ...base, width: "100%" }) }}
                                />
                            </div>
                            <button
                                className="btn btn-outline-danger btn-sm"
                                onClick={() => handleRemoveDirectoryMember(index)}
                            >
                                ×
                            </button>
                        </div>
                    ))}
                    <button
                        className="btn btn-custom-outline btn-sm mb-3"
                        onClick={handleAddDirectoryMember}
                    >
                        + Add Member
                    </button>

                    <h6>Add external emails:</h6>
                    <div className="row mb-2">
                        <div className="col">
                            <input
                                type="email"
                                className="form-control"
                                placeholder="Email"
                                value={externalEmail}
                                onChange={(e) => setExternalEmail(e.target.value)}
                            />
                        </div>
                        <div className="col">
                            <input
                                type="text"
                                className="form-control"
                                placeholder="Name (optional)"
                                value={externalName}
                                onChange={(e) => setExternalName(e.target.value)}
                            />
                        </div>
                        <div className="col-auto">
                            <button className="btn btn-custom-outline" onClick={handleAddExternal}>
                                Add
                            </button>
                        </div>
                    </div>

                    <div className="mb-2">
                        {newGroupData.external_members.map((m, idx) => (
                            <span key={idx} className="badge bg-secondary me-1">
                                {m.name} ({m.email})
                            </span>
                        ))}
                    </div>

                    <button className="btn btn-admin mt-2" onClick={handleCreate}>
                        Save Group
                    </button>
                </div>
            )}

            {/* Active groups */}
            {activeGroups.map((group) => renderGroupCard(group))}

            {/* Toggle archived */}
            {/* <button
                className="btn btn-outline-secondary mb-3"
                onClick={() => setShowArchived(!showArchived)}
            >
                {showArchived ? "Hide Archived Groups" : "Show Archived Groups"}
            </button> */}

            {/* Archived groups */}
            {/* {showArchived && archivedGroups.map((group) => renderGroupCard(group))} */}
        </div>
    );
}
